import type { ApiDashboardElement } from '@highimpact-frontend/api-types';
import type { Layouts } from 'react-grid-layout';
import type { DashboardLayoutProperties } from './dashboard-layout.types';
import type { WidgetType } from './widget.types';

// -- Dashboard grid

export interface DashboardGridType {
  name: string;

  publicUrl?: string;

  additionalProperties?: DashboardLayoutProperties;

  widgets: { [KEY in string]: WidgetType };
  layouts: Layouts;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  params: any;
}

// -- Data addition

export interface NotAddingData {
  _tag: 'NotAddingData';
}

export interface AddingData {
  _tag: 'AddingData';
  editing: number;
}

export interface AwaitingDataAdditionResponse {
  _tag: 'AwaitingDataAdditionResponse';
}

export interface SuccessfulAddition {
  _tag: 'SuccessfulAddition';
}

export interface AdditionFailure {
  _tag: 'AdditionFailure';
}

interface NotConfiguring {
  state: 'NotConfiguring';
}

interface Configuring {
  state: 'Configuring';
  gridId: number;
  widgetId: number;
  gridType: 'grids' | 'reports';
  elementType: Exclude<
    ApiDashboardElement['element_type'],
    'collection' | 'readonlytext'
  >;
  specificWidgetType?: string;
}

interface Reconfiguring extends Omit<Configuring, 'state'> {
  state: 'Reconfiguring';
}

export type ConfigWidgetPanelStateType =
  | NotConfiguring
  | Configuring
  | Reconfiguring;

export type DataAddition =
  | NotAddingData
  | AddingData
  | AwaitingDataAdditionResponse
  | SuccessfulAddition
  | AdditionFailure;

// -- Ressource selector

export interface ResourceSelectorOpen {
  state: 'open';
  dashboardId: number;
  widgetId: string;
  projectId: number;
  siteId: number;
  grid_type: 'reports' | 'grids';
}

export interface ImageGallerySelectorOpen {
  state: 'open';
  dashboardId: number;
  widgetId: string;
  projectId: number;
  siteId: number;
  tags: string[];
  grid_type: 'reports' | 'grids';
}

export interface ConfigClosed {
  state: 'closed';
}

export interface WidgetFormOpen {
  state: 'open';
  elementType: Exclude<
    ApiDashboardElement['element_type'],
    'collection' | 'readonlytext'
  >;
  formTitle: string;
  specificWidgetType?: string;
  widgetGroupId?: string;
}

export type WidgetForm = WidgetFormOpen | ConfigClosed;

export type ResourceSelector = ResourceSelectorOpen | ConfigClosed;

export type ImageGallerySelector = ImageGallerySelectorOpen | ConfigClosed;

// -- Delete widget confirmation

export interface DeleteWidgetConfirmationOpen {
  state: 'open';
  dashboardElementId: number;
  widgetName: string;
}

export type DeleteWidgetConfirmation =
  | DeleteWidgetConfirmationOpen
  | ConfigClosed;

// -- Dashboard form

export type DashboardFormState =
  | 'DashboardFormClosed'
  | 'UserDashboardFormOpen'
  | 'ReportDashboardFormOpen';

// -- widget editing

interface NotEditingState {
  state: 'NotEditing';
}

interface EditingWithoutDisplayerState {
  state: 'EditingWithoutDisplayer';
  gridType: 'grids' | 'reports';
  gridId: number;
  widgetId: string;
}

interface EditingWithDisplayerState {
  state: 'EditingWithDisplayer';
  gridType: 'grids' | 'reports';
  gridId: number;
  widgetId: string;
  displayerId: number;
}

export type EditingState =
  | NotEditingState
  | EditingWithoutDisplayerState
  | EditingWithDisplayerState;

// -- slice

export interface DashboardStoreType {
  text: string | null;

  editing: EditingState;
  addingData: DataAddition;
  ConfigWidgetPanelState: ConfigWidgetPanelStateType;
  dashboardForm: DashboardFormState;
  widgetFormIsOpen: WidgetForm;
  deleteConfirmationModalIsOpen: DeleteWidgetConfirmation;
  resourceSelectorConfig: ResourceSelector;
  imageGallerySelectorConfig: ImageGallerySelector;

  colsNumber: number;
  rowsNumber: number;

  currentGridWidth: number;
  currentGridHeight: number;

  grids: { [KEY in number]: DashboardGridType };
  reports: { [KEY in number]: DashboardGridType };
}

export const initialDashboard: DashboardStoreType = {
  text: null,

  editing: {
    state: 'NotEditing',
  },

  ConfigWidgetPanelState: {
    state: 'NotConfiguring',
  },

  addingData: {
    _tag: 'NotAddingData',
  },

  dashboardForm: 'DashboardFormClosed',
  widgetFormIsOpen: { state: 'closed' },
  deleteConfirmationModalIsOpen: { state: 'closed' },
  resourceSelectorConfig: { state: 'closed' },
  imageGallerySelectorConfig: { state: 'closed' },

  colsNumber: 24,
  rowsNumber: 32,

  currentGridHeight: 0,
  currentGridWidth: 0,

  grids: {},
  reports: {},
};
